"use client";

import type { QueryClient } from "@tanstack/react-query";
import { DATA_KEYS } from "@/utils/useRemoteData";

// What the agent and flow Tests pages read.
const TEST_KEYS: DATA_KEYS[] = [
  DATA_KEYS.AGENT_TEST_SUITES,
  DATA_KEYS.TEST_SUITES,
  DATA_KEYS.TEST_SUITE,
  DATA_KEYS.TEST_CASES,
  DATA_KEYS.TEST_CASE,
  DATA_KEYS.EVALUATORS,
];

const TEST_RUN_KEYS: DATA_KEYS[] = [
  ...TEST_KEYS,
  DATA_KEYS.EVALUATION_RUNS,
  DATA_KEYS.EVALUATION_RUN_REPORT,
  DATA_KEYS.TEST_SUITE_EVALUATION_RESULTS,
  DATA_KEYS.TEST_CASE_RUNS,
];

const IMPORTED_DEPENDENCY_KEYS: DATA_KEYS[] = [
  DATA_KEYS.KNOWLEDGE_BASES_V2,
  DATA_KEYS.TABLES,
  DATA_KEYS.INBOXES,
  DATA_KEYS.TRIGGERS,
  DATA_KEYS.LIST_DEPLOYMENTS,
  DATA_KEYS.INTEGRATIONS,
];

export const TOOL_INVALIDATIONS: Partial<Record<string, DATA_KEYS[]>> = {
  // WORKSHOP_RECENT is the home page's recent items.
  noxus_workflows_update: [
    DATA_KEYS.WORKFLOWS,
    DATA_KEYS.WORKFLOWS_ID,
    DATA_KEYS.WORKSHOP_RECENT,
  ],
  noxus_workflows_delete: [
    DATA_KEYS.WORKFLOWS,
    DATA_KEYS.WORKFLOWS_ID,
    DATA_KEYS.WORKSHOP_RECENT,
    DATA_KEYS.TRIGGERS,
    DATA_KEYS.TAGS,
    DATA_KEYS.ALL_RUNS,
  ],
  // Brings the flow back with its runs.
  noxus_workflows_restore: [
    DATA_KEYS.WORKFLOWS,
    DATA_KEYS.WORKFLOWS_ID,
    DATA_KEYS.WORKSHOP_RECENT,
    DATA_KEYS.WORKFLOW_RUNS,
    DATA_KEYS.ALL_RUNS,
    DATA_KEYS.RUNS_SEARCH,
  ],
  noxus_workflows_build: [
    DATA_KEYS.WORKFLOWS,
    DATA_KEYS.WORKFLOWS_ID,
    DATA_KEYS.WORKSHOP_RECENT,
  ],
  noxus_workflows_edit_nodes: [DATA_KEYS.WORKFLOWS, DATA_KEYS.WORKFLOWS_ID],
  noxus_workflows_save_version: [DATA_KEYS.WORKFLOW_VERSIONS],
  // The Flows list shows each flow's triggers.
  noxus_workflows_create_trigger: [
    DATA_KEYS.TRIGGERS,
    DATA_KEYS.WORKFLOWS,
    DATA_KEYS.WORKFLOWS_ID,
  ],
  noxus_workflows_run_partial: [
    DATA_KEYS.WORKFLOW_RUNS,
    DATA_KEYS.ALL_RUNS,
    DATA_KEYS.RUNS_SEARCH,
    DATA_KEYS.RUN,
    DATA_KEYS.WORKFLOWS,
  ],
  noxus_v1_workflows_create: [DATA_KEYS.WORKFLOWS, DATA_KEYS.WORKFLOWS_ID],
  noxus_v1_workflows_add_node: [DATA_KEYS.WORKFLOWS, DATA_KEYS.WORKFLOWS_ID],
  noxus_v1_workflows_link_nodes: [DATA_KEYS.WORKFLOWS, DATA_KEYS.WORKFLOWS_ID],
  noxus_v1_workflows_edit_node: [DATA_KEYS.WORKFLOWS, DATA_KEYS.WORKFLOWS_ID],
  noxus_v1_workflows_remove_node: [DATA_KEYS.WORKFLOWS, DATA_KEYS.WORKFLOWS_ID],
  noxus_v1_workflows_autolayout: [DATA_KEYS.WORKFLOWS, DATA_KEYS.WORKFLOWS_ID],
  noxus_workflows_run: [
    DATA_KEYS.WORKFLOW_RUNS,
    DATA_KEYS.ALL_RUNS,
    DATA_KEYS.RUNS_SEARCH,
    DATA_KEYS.RUN,
    DATA_KEYS.WORKFLOWS,
  ],

  noxus_agents_create: [
    DATA_KEYS.ASSISTANTS,
    DATA_KEYS.ASSISTANT_ID,
    DATA_KEYS.WORKSHOP_RECENT,
  ],
  noxus_agents_update: [
    DATA_KEYS.ASSISTANTS,
    DATA_KEYS.ASSISTANT_ID,
    DATA_KEYS.WORKSHOP_RECENT,
  ],
  noxus_agents_delete: [
    DATA_KEYS.ASSISTANTS,
    DATA_KEYS.ASSISTANT_ID,
    DATA_KEYS.WORKSHOP_RECENT,
    DATA_KEYS.CONVERSATIONS,
    DATA_KEYS.LIST_DEPLOYMENTS,
  ],
  // The handler refetches the agent before the form takes it.
  noxus_agents_restore_version: [
    DATA_KEYS.ASSISTANTS,
    DATA_KEYS.ASSISTANT_VERSIONS,
  ],
  // The Deploy tab's own query is refreshed by the handler; these are the
  // Apps page and the agent's insights.
  noxus_deployments_create: [DATA_KEYS.LIST_DEPLOYMENTS],
  noxus_deployments_activate: [DATA_KEYS.LIST_DEPLOYMENTS],
  noxus_deployments_deactivate: [DATA_KEYS.LIST_DEPLOYMENTS],
  noxus_deployments_delete: [DATA_KEYS.LIST_DEPLOYMENTS],

  noxus_conversations_create: [DATA_KEYS.CONVERSATIONS],
  noxus_conversations_update: [DATA_KEYS.CONVERSATION, DATA_KEYS.CONVERSATIONS],
  noxus_conversations_delete: [DATA_KEYS.CONVERSATIONS, DATA_KEYS.CONVERSATION],
  noxus_conversations_chat: [DATA_KEYS.CONVERSATION, DATA_KEYS.CONVERSATIONS],
  noxus_conversations_regenerate: [
    DATA_KEYS.CONVERSATION,
    DATA_KEYS.CONVERSATIONS,
  ],
  noxus_conversations_rate_message: [DATA_KEYS.CONVERSATION],
  noxus_conversations_stop: [DATA_KEYS.CONVERSATION],
  noxus_conversations_delete_matching: [
    DATA_KEYS.CONVERSATIONS,
    DATA_KEYS.CONVERSATION,
  ],

  noxus_knowledge_bases_create: [
    DATA_KEYS.KNOWLEDGE_BASES_V2,
    DATA_KEYS.WORKSHOP_RECENT,
  ],
  noxus_knowledge_bases_update: [
    DATA_KEYS.KNOWLEDGE_BASES_V2,
    DATA_KEYS.KNOWLEDGE_BASES_V2_ID,
  ],
  noxus_knowledge_bases_delete: [
    DATA_KEYS.KNOWLEDGE_BASES_V2,
    DATA_KEYS.KNOWLEDGE_BASES_V2_ID,
    DATA_KEYS.WORKSHOP_RECENT,
  ],
  noxus_knowledge_bases_generate_summary: [DATA_KEYS.KNOWLEDGE_BASES_V2_ID],
  noxus_knowledge_bases_update_search: [DATA_KEYS.KNOWLEDGE_BASES_V2_ID],
  noxus_knowledge_bases_repair_health: [DATA_KEYS.KNOWLEDGE_BASES_V2_ID],
  noxus_knowledge_bases_upload_documents: [
    DATA_KEYS.KNOWLEDGE_BASES_V2_ID,
    DATA_KEYS.KNOWLEDGE_BASE_INGESTION_JOBS,
    DATA_KEYS.KNOWLEDGE_BASE_DOCUMENTS,
  ],
  noxus_knowledge_bases_delete_documents: [
    DATA_KEYS.KNOWLEDGE_BASES_V2_ID,
    DATA_KEYS.KNOWLEDGE_BASE_INGESTION_JOBS,
    DATA_KEYS.KNOWLEDGE_BASE_DOCUMENTS,
    DATA_KEYS.KB_SYNCS,
  ],
  noxus_knowledge_bases_create_folder: [
    DATA_KEYS.KNOWLEDGE_BASES_V2_ID,
    DATA_KEYS.KNOWLEDGE_BASE_DOCUMENTS,
  ],
  noxus_knowledge_bases_move_documents: [
    DATA_KEYS.KNOWLEDGE_BASES_V2_ID,
    DATA_KEYS.KNOWLEDGE_BASE_DOCUMENTS,
  ],
  noxus_knowledge_bases_retry_documents: [
    DATA_KEYS.KNOWLEDGE_BASES_V2_ID,
    DATA_KEYS.KNOWLEDGE_BASE_INGESTION_JOBS,
  ],
  noxus_knowledge_bases_add_website: [
    DATA_KEYS.KNOWLEDGE_BASES_V2_ID,
    DATA_KEYS.KNOWLEDGE_BASE_INGESTION_JOBS,
    DATA_KEYS.KNOWLEDGE_BASE_DOCUMENTS,
    DATA_KEYS.KB_SYNCS,
  ],
  noxus_knowledge_bases_run_sync: [
    DATA_KEYS.KB_SYNCS,
    DATA_KEYS.KNOWLEDGE_BASE_INGESTION_JOBS,
  ],
  noxus_knowledge_bases_enable_sync: [DATA_KEYS.KB_SYNCS],
  noxus_knowledge_bases_update_sync: [DATA_KEYS.KB_SYNCS],
  noxus_knowledge_bases_stop_sync: [DATA_KEYS.KB_SYNCS],

  noxus_tables_create: [DATA_KEYS.TABLES],
  noxus_tables_update: [DATA_KEYS.TABLES, DATA_KEYS.TABLES_ID],
  noxus_tables_add_column: [DATA_KEYS.TABLES_ID, DATA_KEYS.TABLES],
  noxus_tables_drop_column: [
    DATA_KEYS.TABLES_ID,
    DATA_KEYS.TABLE_ROWS,
    DATA_KEYS.TABLES,
  ],
  noxus_tables_insert_rows: [DATA_KEYS.TABLE_ROWS, DATA_KEYS.TABLES],
  noxus_tables_update_rows: [DATA_KEYS.TABLE_ROWS, DATA_KEYS.TABLES],
  noxus_tables_delete_rows: [DATA_KEYS.TABLE_ROWS, DATA_KEYS.TABLES],
  noxus_tables_run_statement: [DATA_KEYS.TABLE_ROWS, DATA_KEYS.TABLES],
  noxus_tables_import_csv: [DATA_KEYS.TABLE_ROWS, DATA_KEYS.TABLES],
  noxus_tables_delete: [DATA_KEYS.TABLES, DATA_KEYS.TABLES_ID],
  noxus_tables_clear_rows: [DATA_KEYS.TABLE_ROWS, DATA_KEYS.TABLES],
  noxus_tables_update_column: [
    DATA_KEYS.TABLES_ID,
    DATA_KEYS.TABLE_ROWS,
    DATA_KEYS.TABLES,
  ],
  noxus_tables_duplicate_rows: [DATA_KEYS.TABLE_ROWS, DATA_KEYS.TABLES],

  // Case management. The sidebar's inbox list (INBOXES) carries open-case
  // counts, so a write that adds, removes or moves a case refreshes it. The
  // open pages read INBOXES_ID (inbox and its properties), CASES (the cases
  // list), CASES_ID (a case), TASKS (the tasks lists) and TASK_DETAIL (a task).
  noxus_cases_enable_module: [DATA_KEYS.GROUP, DATA_KEYS.GROUPS],
  noxus_inboxes_create: [DATA_KEYS.INBOXES],
  noxus_inboxes_configure: [DATA_KEYS.INBOXES, DATA_KEYS.INBOXES_ID],
  noxus_inboxes_disconnect_source: [DATA_KEYS.INBOXES, DATA_KEYS.INBOXES_ID],
  noxus_inboxes_delete: [
    DATA_KEYS.INBOXES,
    DATA_KEYS.INBOXES_ID,
    DATA_KEYS.CASES,
    DATA_KEYS.TASKS,
  ],
  noxus_inboxes_create_field: [DATA_KEYS.INBOXES_ID, DATA_KEYS.CASES_ID],
  noxus_inboxes_update_field: [DATA_KEYS.INBOXES_ID, DATA_KEYS.CASES_ID],
  noxus_inboxes_delete_field: [
    DATA_KEYS.INBOXES_ID,
    DATA_KEYS.CASES_ID,
    DATA_KEYS.TASK_DETAIL,
  ],
  noxus_inboxes_reorder_fields: [DATA_KEYS.INBOXES_ID, DATA_KEYS.CASES_ID],
  noxus_inboxes_rename_group: [DATA_KEYS.INBOXES_ID, DATA_KEYS.CASES_ID],
  noxus_inboxes_delete_group: [DATA_KEYS.INBOXES_ID, DATA_KEYS.CASES_ID],
  noxus_inboxes_simulate_email: [
    DATA_KEYS.CASES,
    DATA_KEYS.TASKS,
    DATA_KEYS.INBOXES,
  ],
  noxus_inboxes_export: [DATA_KEYS.FILE_METADATA],
  noxus_inboxes_import: [DATA_KEYS.WORKFLOWS, ...IMPORTED_DEPENDENCY_KEYS],
  noxus_cases_create: [DATA_KEYS.CASES, DATA_KEYS.INBOXES],
  // A task page shows its case, so case writes refresh TASK_DETAIL too.
  noxus_cases_update: [
    DATA_KEYS.CASES,
    DATA_KEYS.CASES_ID,
    DATA_KEYS.TASK_DETAIL,
    DATA_KEYS.INBOXES,
  ],
  noxus_cases_clone: [DATA_KEYS.CASES, DATA_KEYS.TASKS, DATA_KEYS.INBOXES],
  noxus_cases_move: [
    DATA_KEYS.CASES,
    DATA_KEYS.CASES_ID,
    DATA_KEYS.TASKS,
    DATA_KEYS.TASK_DETAIL,
    DATA_KEYS.INBOXES,
  ],
  noxus_cases_reprocess: [
    DATA_KEYS.CASES,
    DATA_KEYS.CASES_ID,
    DATA_KEYS.TASKS,
    DATA_KEYS.TASK_DETAIL,
    DATA_KEYS.INBOXES,
  ],
  noxus_cases_delete: [
    DATA_KEYS.CASES,
    DATA_KEYS.CASES_ID,
    DATA_KEYS.TASKS,
    DATA_KEYS.TASK_DETAIL,
    DATA_KEYS.INBOXES,
  ],
  noxus_cases_delete_all: [
    DATA_KEYS.CASES,
    DATA_KEYS.CASES_ID,
    DATA_KEYS.TASKS,
    DATA_KEYS.TASK_DETAIL,
    DATA_KEYS.INBOXES,
  ],
  // A new task puts its case back to pending.
  noxus_cases_create_task: [
    DATA_KEYS.TASKS,
    DATA_KEYS.CASES,
    DATA_KEYS.CASES_ID,
  ],
  noxus_cases_assign_task: [DATA_KEYS.TASKS, DATA_KEYS.TASK_DETAIL],

  noxus_workspaces_create: [
    DATA_KEYS.GROUPS,
    DATA_KEYS.MY_GROUPS,
    DATA_KEYS.ALL_GROUPS,
  ],
  // GROUP_USERS is the members table itself; the GROUPS keys are the
  // workspace switcher, which a membership change also moves.
  noxus_workspaces_add_member: [
    DATA_KEYS.GROUP_USERS,
    DATA_KEYS.GROUPS,
    DATA_KEYS.MY_GROUPS,
    DATA_KEYS.ALL_GROUPS,
  ],
  noxus_workspaces_remove_member: [
    DATA_KEYS.GROUP_USERS,
    DATA_KEYS.GROUPS,
    DATA_KEYS.MY_GROUPS,
    DATA_KEYS.ALL_GROUPS,
  ],
  // As the Members tab's role menu: the user's own role sets their permissions.
  noxus_workspaces_set_member_role: [
    DATA_KEYS.GROUP_USERS,
    DATA_KEYS.GROUPS,
    DATA_KEYS.MY_PERMISSIONS,
  ],
  // As the Roles page's create: its lists, a workspace's role menus, and the
  // user's own permissions should the role be theirs.
  noxus_roles_create: [
    DATA_KEYS.ROLES_PAGE_WORKSPACE,
    DATA_KEYS.ROLES_PAGE_ORGANIZATION,
    DATA_KEYS.ROLES_WORKSPACE,
    DATA_KEYS.MY_PERMISSIONS,
  ],
  // GROUP is the settings page and its About card; the GROUPS keys are the
  // workspace switcher and the workspaces list, which show the name and colour.
  noxus_workspaces_update: [
    DATA_KEYS.GROUP,
    DATA_KEYS.GROUPS,
    DATA_KEYS.MY_GROUPS,
    DATA_KEYS.ALL_GROUPS,
  ],
  // A bundle carries its dependencies, so an import can create knowledge
  // bases, tables, inboxes, triggers, deployments and connections alongside
  // the workflow or agent named on the tool. The effect API only gets the
  // tool name, so both imports invalidate the whole dependency set.
  noxus_workflows_import: [
    DATA_KEYS.WORKFLOWS,
    DATA_KEYS.WORKFLOWS_ID,
    ...IMPORTED_DEPENDENCY_KEYS,
  ],
  noxus_agents_save_version: [DATA_KEYS.ASSISTANT_VERSIONS],
  noxus_agents_duplicate: [DATA_KEYS.ASSISTANTS, DATA_KEYS.WORKSHOP_RECENT],
  noxus_agents_import: [
    DATA_KEYS.ASSISTANTS,
    DATA_KEYS.ASSISTANT_ID,
    DATA_KEYS.WORKSHOP_RECENT,
    ...IMPORTED_DEPENDENCY_KEYS,
  ],

  noxus_files_upload: [DATA_KEYS.FILE_METADATA],

  noxus_runs_delete: [
    DATA_KEYS.WORKFLOW_RUNS,
    DATA_KEYS.ALL_RUNS,
    DATA_KEYS.RUNS_SEARCH,
    DATA_KEYS.RUN,
  ],

  noxus_workspaces_create_key: [DATA_KEYS.APIKEYS],
  // Usage controls, the limits dialog's usage and the Billing overview's alerts.
  noxus_billing_add_usage_control: [
    DATA_KEYS.BILLING_LIMITS,
    DATA_KEYS.BILLING,
  ],
  noxus_billing_edit_usage_control: [
    DATA_KEYS.BILLING_LIMITS,
    DATA_KEYS.BILLING,
  ],
  noxus_billing_delete_usage_control: [
    DATA_KEYS.BILLING_LIMITS,
    DATA_KEYS.BILLING,
  ],

  noxus_evaluations_create_agent_test_suite: TEST_KEYS,
  noxus_evaluations_create_evaluator: TEST_KEYS,
  noxus_evaluations_update_evaluator: TEST_KEYS,
  noxus_evaluations_delete_evaluator: TEST_KEYS,
  noxus_evaluations_create_test_case: TEST_KEYS,
  noxus_evaluations_create_test_cases: TEST_KEYS,
  noxus_evaluations_create_flow_test_cases: TEST_KEYS,
  noxus_evaluations_create_test_from_conversation: TEST_KEYS,
  noxus_evaluations_create_tests_from_conversations: TEST_KEYS,
  noxus_evaluations_update_test_case: TEST_KEYS,
  noxus_evaluations_duplicate_test_case: TEST_KEYS,
  noxus_evaluations_delete_test_case: TEST_KEYS,
  noxus_evaluations_update_test_suite: TEST_KEYS,
  noxus_evaluations_delete_test_suite: TEST_KEYS,
  noxus_evaluations_run: TEST_RUN_KEYS,
  noxus_evaluations_wait_run: TEST_RUN_KEYS,
  noxus_evaluations_cancel_run: TEST_RUN_KEYS,
  noxus_evaluations_create_workflow_test_suite: TEST_KEYS,
  noxus_evaluations_create_case_from_run: TEST_KEYS,
};

// Tools that write nothing a page caches, so they refresh nothing. A tool
// missing from both this list and the maps above fails the coverage test.
export const TOOLS_WITHOUT_CACHED_STATE: ReadonlySet<string> = new Set([
  // Returns a file; changes nothing.
  "noxus_files_download",
  // Creates a CSV the chat presents; the table is unchanged.
  "noxus_tables_export",
  // Sandboxes have no page, and an agent's shell reads its sandbox live.
  "noxus_sandboxes_create",
  "noxus_sandboxes_delete",
  "noxus_sandboxes_reset",
  "noxus_sandboxes_run_command",
  "noxus_sandboxes_write_file",
]);

export function invalidateForTool(
  queryClient: QueryClient,
  toolName: string
): void {
  const keys = TOOL_INVALIDATIONS[toolName];
  if (!keys) return;
  for (const key of keys) {
    queryClient.invalidateQueries({ queryKey: [key] });
  }
}
